// Full legal content for O'Lime Design (FR / IT / EN).
// Factual placeholders ({SIRET}, {EMAIL}, {PHONE}, ...) are interpolated from the
// site configuration (editable from the admin panel). Empty values render as "—".

export const LEGAL_TYPES = ["mentions", "cgv", "confidentialite", "cookies"];

export const LEGAL_ORDER = {
  fr: [
    { type: "mentions", label: "Mentions légales" },
    { type: "cgv", label: "Conditions générales de vente" },
    { type: "confidentialite", label: "Politique de confidentialité" },
    { type: "cookies", label: "Politique des cookies" },
  ],
  it: [
    { type: "mentions", label: "Note legali" },
    { type: "cgv", label: "Condizioni generali di vendita" },
    { type: "confidentialite", label: "Informativa sulla privacy" },
    { type: "cookies", label: "Politica dei cookie" },
  ],
  en: [
    { type: "mentions", label: "Legal Notice" },
    { type: "cgv", label: "Terms & Conditions" },
    { type: "confidentialite", label: "Privacy Policy" },
    { type: "cookies", label: "Cookies Policy" },
  ],
};

export const LEGAL_UI = {
  fr: {
    updated: "Dernière mise à jour",
    banner: {
      text: "J’utilise des cookies pour assurer le bon fonctionnement du site et, avec votre accord, en mesurer l’audience. Aucun cookie non essentiel n’est déposé sans votre consentement.",
      accept: "Accepter",
      refuse: "Refuser",
      customize: "Personnaliser",
      save: "Enregistrer mes choix",
      policy: "Politique des cookies",
      necessary: "Cookies nécessaires",
      necessaryDesc: "Indispensables au fonctionnement du site. Toujours actifs.",
      analytics: "Cookies analytiques",
      analyticsDesc: "Pour mesurer l’audience de façon anonyme.",
      functional: "Cookies fonctionnels",
      functionalDesc: "Pour mémoriser vos préférences (langue, affichage).",
      manage: "Gérer les cookies",
    },
    consent: {
      label: "J’ai lu la Politique de confidentialité et j’accepte que les informations saisies soient utilisées afin de répondre à ma demande.",
      link: "Politique de confidentialité",
      required: "Vous devez accepter la Politique de confidentialité.",
    },
  },
  it: {
    updated: "Ultimo aggiornamento",
    banner: {
      text: "Utilizzo dei cookie per garantire il corretto funzionamento del sito e, con il vostro consenso, per misurarne l’audience. Nessun cookie non essenziale viene installato senza il vostro consenso.",
      accept: "Accetta",
      refuse: "Rifiuta",
      customize: "Personalizza",
      save: "Salva le mie scelte",
      policy: "Politica dei cookie",
      necessary: "Cookie necessari",
      necessaryDesc: "Indispensabili al funzionamento del sito. Sempre attivi.",
      analytics: "Cookie analitici",
      analyticsDesc: "Per misurare l’audience in modo anonimo.",
      functional: "Cookie funzionali",
      functionalDesc: "Per memorizzare le vostre preferenze (lingua, visualizzazione).",
      manage: "Gestisci i cookie",
    },
    consent: {
      label: "Ho letto l’Informativa sulla privacy e acconsento all’utilizzo dei dati inseriti per rispondere alla mia richiesta.",
      link: "Informativa sulla privacy",
      required: "Devi accettare l’Informativa sulla privacy.",
    },
  },
  en: {
    updated: "Last updated",
    banner: {
      text: "I use cookies to ensure the site works properly and, with your consent, to measure its audience. No non-essential cookie is set without your consent.",
      accept: "Accept",
      refuse: "Decline",
      customize: "Customise",
      save: "Save my choices",
      policy: "Cookies Policy",
      necessary: "Necessary cookies",
      necessaryDesc: "Essential for the site to work. Always active.",
      analytics: "Analytics cookies",
      analyticsDesc: "To measure the audience anonymously.",
      functional: "Functional cookies",
      functionalDesc: "To remember your preferences (language, display).",
      manage: "Manage cookies",
    },
    consent: {
      label: "I have read the Privacy Policy and I agree that the information I provide will be used to respond to my request.",
      link: "Privacy Policy",
      required: "You must accept the Privacy Policy.",
    },
  },
};

const DOCS = {
  mentions: {
    fr: {
      seoTitle: "Mentions légales — O'Lime Design",
      seoDesc: "Mentions légales du site O'Lime Design : éditeur, hébergeur, propriété intellectuelle et droit applicable.",
      updated: "Juin 2026",
      sections: [
        { h: "Éditeur du site", p: [
          "{COMPANY}",
          "Entreprise individuelle (micro-entreprise).",
          "Fondatrice : {FOUNDER}.",
          "SIRET : {SIRET}",
          "Code APE : {APE} — Activités spécialisées de design.",
          "TVA : {VAT}.",
          "Localisation : {CITY}.",
          "E-mail : {EMAIL}",
          "Téléphone : {PHONE}",
        ]},
        { h: "Directeur de la publication", p: ["{FOUNDER}."] },
        { h: "Hébergeur", p: ["{HOST}", "{HOST_ADDR}"] },
        { h: "Propriété intellectuelle", p: [
          "L’ensemble des contenus présents sur ce site (textes, photographies, rendus 3D, illustrations, logos, vidéos et éléments graphiques) est protégé par le Code de la propriété intellectuelle et demeure la propriété exclusive de {COMPANY}, sauf mention contraire.",
          "Toute reproduction, représentation, modification, publication ou adaptation, totale ou partielle, sans autorisation écrite préalable, est interdite et constitue une contrefaçon sanctionnée par la loi.",
        ]},
        { h: "Responsabilité", p: [
          "Les informations publiées sur ce site sont fournies à titre indicatif et peuvent évoluer sans préavis. {COMPANY} s’efforce d’assurer l’exactitude des informations diffusées mais ne saurait être tenue responsable des erreurs, omissions ou d’une indisponibilité temporaire du site.",
        ]},
        { h: "Droit applicable", p: [
          "Le présent site est soumis au droit français. Tout litige relatif à son utilisation relève de la compétence des juridictions françaises.",
        ]},
      ],
    },
    it: {
      seoTitle: "Note legali — O'Lime Design",
      seoDesc: "Note legali del sito O'Lime Design: editore, hosting, proprietà intellettuale e legge applicabile.",
      updated: "Giugno 2026",
      sections: [
        { h: "Editore del sito", p: [
          "{COMPANY}",
          "Impresa individuale (regime forfettario).",
          "Fondatrice: {FOUNDER}.",
          "SIRET: {SIRET}",
          "Codice APE: {APE} — Attività specializzate di design.",
          "IVA: {VAT}.",
          "Sede: {CITY}.",
          "E-mail: {EMAIL}",
          "Telefono: {PHONE}",
        ]},
        { h: "Direttore della pubblicazione", p: ["{FOUNDER}."] },
        { h: "Hosting", p: ["{HOST}", "{HOST_ADDR}"] },
        { h: "Proprietà intellettuale", p: [
          "Tutti i contenuti presenti su questo sito (testi, fotografie, render 3D, illustrazioni, loghi, video ed elementi grafici) sono protetti dal diritto d’autore e restano proprietà esclusiva di {COMPANY}, salvo diversa indicazione.",
          "Qualsiasi riproduzione, rappresentazione, modifica, pubblicazione o adattamento, totale o parziale, senza previa autorizzazione scritta, è vietata e costituisce una contraffazione perseguibile per legge.",
        ]},
        { h: "Responsabilità", p: [
          "Le informazioni pubblicate su questo sito sono fornite a titolo indicativo e possono variare senza preavviso. {COMPANY} si adopera per garantirne l’esattezza ma non può essere ritenuta responsabile di errori, omissioni o di un’indisponibilità temporanea del sito.",
        ]},
        { h: "Legge applicabile", p: [
          "Il presente sito è soggetto al diritto francese. Ogni controversia relativa al suo utilizzo è di competenza dei tribunali francesi.",
        ]},
      ],
    },
    en: {
      seoTitle: "Legal Notice — O'Lime Design",
      seoDesc: "Legal notice for the O'Lime Design website: publisher, host, intellectual property and applicable law.",
      updated: "June 2026",
      sections: [
        { h: "Site Publisher", p: [
          "{COMPANY}",
          "Sole proprietorship (micro-enterprise).",
          "Founder: {FOUNDER}.",
          "SIRET: {SIRET}",
          "APE code: {APE} — Specialised design activities.",
          "VAT: {VAT}.",
          "Location: {CITY}.",
          "Email: {EMAIL}",
          "Phone: {PHONE}",
        ]},
        { h: "Publication Director", p: ["{FOUNDER}."] },
        { h: "Hosting", p: ["{HOST}", "{HOST_ADDR}"] },
        { h: "Intellectual Property", p: [
          "All content on this website (text, photographs, 3D renders, illustrations, logos, videos and graphic elements) is protected by intellectual property law and remains the exclusive property of {COMPANY}, unless otherwise stated.",
          "Any reproduction, representation, modification, publication or adaptation, in whole or in part, without prior written authorisation, is prohibited and constitutes an infringement punishable by law.",
        ]},
        { h: "Liability", p: [
          "The information published on this site is provided for guidance only and may change without notice. {COMPANY} endeavours to ensure the accuracy of the information but cannot be held liable for errors, omissions or any temporary unavailability of the site.",
        ]},
        { h: "Applicable Law", p: [
          "This website is governed by French law. Any dispute relating to its use falls under the jurisdiction of the French courts.",
        ]},
      ],
    },
  },

  confidentialite: {
    fr: {
      seoTitle: "Politique de confidentialité — O'Lime Design",
      seoDesc: "Politique de confidentialité RGPD d'O'Lime Design : données collectées, finalités, base légale, durée de conservation et droits.",
      updated: "Juin 2026",
      sections: [
        { h: "Responsable du traitement", p: [
          "{COMPANY} — {FOUNDER}.",
          "Pour toute question relative à vos données : {EMAIL}",
        ]},
        { h: "Données collectées", p: [
          "Via le formulaire de contact, les données suivantes peuvent être collectées : nom, prénom, adresse e-mail, téléphone, pays, langue, type de projet, localisation du projet, surface, budget, délai souhaité, message et documents éventuellement transmis.",
        ]},
        { h: "Finalité du traitement", p: [
          "Les données sont utilisées uniquement pour : répondre à vos demandes de contact, établir un devis, organiser un rendez-vous et assurer le suivi des projets. Vos données ne sont jamais revendues.",
        ]},
        { h: "Base légale", p: [
          "Le traitement repose sur votre consentement (case à cocher du formulaire) et sur les mesures précontractuelles nécessaires lorsqu’une demande de devis est effectuée.",
        ]},
        { h: "Durée de conservation", p: [
          "Les données sont conservées le temps nécessaire au traitement de votre demande, puis pour une durée maximale de trois (3) ans à compter du dernier contact à des fins de suivi commercial. Les données liées à un contrat sont conservées conformément aux obligations légales et comptables en vigueur.",
        ]},
        { h: "Destinataires", p: [
          "Les données sont destinées exclusivement à {COMPANY} et aux prestataires techniques strictement nécessaires au fonctionnement du site (hébergement, service d’e-mail, outil de prise de rendez-vous). Ces prestataires agissent en tant que sous-traitants et sont tenus à la confidentialité.",
        ]},
        { h: "Vos droits", p: [
          "Conformément au RGPD, vous disposez d’un droit d’accès, de rectification, d’effacement, d’opposition, de limitation et de portabilité de vos données.",
          "Vous pouvez exercer ces droits à tout moment en écrivant à {EMAIL}.",
          "Vous disposez également du droit d’introduire une réclamation auprès de la CNIL (Commission Nationale de l’Informatique et des Libertés — www.cnil.fr).",
        ]},
        { h: "Sécurité", p: [
          "Toutes les mesures techniques et organisationnelles raisonnables sont mises en œuvre afin de protéger vos données personnelles contre la perte, l’accès non autorisé, la divulgation ou l’altération.",
        ]},
      ],
    },
    it: {
      seoTitle: "Informativa sulla privacy — O'Lime Design",
      seoDesc: "Informativa privacy GDPR di O'Lime Design: dati raccolti, finalità, base giuridica, conservazione e diritti.",
      updated: "Giugno 2026",
      sections: [
        { h: "Titolare del trattamento", p: [
          "{COMPANY} — {FOUNDER}.",
          "Per qualsiasi domanda relativa ai vostri dati: {EMAIL}",
        ]},
        { h: "Dati raccolti", p: [
          "Tramite il modulo di contatto possono essere raccolti i seguenti dati: nome, cognome, e-mail, telefono, paese, lingua, tipo di progetto, luogo del progetto, superficie, budget, tempistiche desiderate, messaggio ed eventuali documenti trasmessi.",
        ]},
        { h: "Finalità del trattamento", p: [
          "I dati sono utilizzati unicamente per: rispondere alle vostre richieste di contatto, redigere un preventivo, organizzare un appuntamento e seguire i progetti. I vostri dati non vengono mai rivenduti.",
        ]},
        { h: "Base giuridica", p: [
          "Il trattamento si fonda sul vostro consenso (casella da spuntare nel modulo) e sulle misure precontrattuali necessarie in caso di richiesta di preventivo.",
        ]},
        { h: "Periodo di conservazione", p: [
          "I dati sono conservati per il tempo necessario a gestire la vostra richiesta e, successivamente, per un massimo di tre (3) anni dall’ultimo contatto a fini di follow-up commerciale. I dati contrattuali sono conservati nel rispetto degli obblighi legali e contabili vigenti.",
        ]},
        { h: "Destinatari", p: [
          "I dati sono destinati esclusivamente a {COMPANY} e ai fornitori tecnici strettamente necessari al funzionamento del sito (hosting, servizio e-mail, strumento di prenotazione), che agiscono come responsabili del trattamento e sono tenuti alla riservatezza.",
        ]},
        { h: "I vostri diritti", p: [
          "Ai sensi del GDPR, avete diritto di accesso, rettifica, cancellazione, opposizione, limitazione e portabilità dei vostri dati.",
          "Potete esercitare tali diritti in qualsiasi momento scrivendo a {EMAIL}.",
          "Avete inoltre il diritto di presentare reclamo all’autorità di controllo competente (in Francia, la CNIL — www.cnil.fr).",
        ]},
        { h: "Sicurezza", p: [
          "Vengono adottate tutte le misure tecniche e organizzative ragionevoli per proteggere i vostri dati personali da perdita, accesso non autorizzato, divulgazione o alterazione.",
        ]},
      ],
    },
    en: {
      seoTitle: "Privacy Policy — O'Lime Design",
      seoDesc: "GDPR privacy policy for O'Lime Design: data collected, purposes, legal basis, retention and your rights.",
      updated: "June 2026",
      sections: [
        { h: "Data Controller", p: [
          "{COMPANY} — {FOUNDER}.",
          "For any question regarding your data: {EMAIL}",
        ]},
        { h: "Data Collected", p: [
          "Through the contact form, the following data may be collected: first name, last name, email address, phone, country, language, project type, project location, surface area, budget, desired timeframe, message and any documents provided.",
        ]},
        { h: "Purpose of Processing", p: [
          "Data is used solely to: respond to your enquiries, prepare a quote, arrange an appointment and follow up on projects. Your data is never sold.",
        ]},
        { h: "Legal Basis", p: [
          "Processing is based on your consent (the form checkbox) and on the pre-contractual measures required when a quote is requested.",
        ]},
        { h: "Retention Period", p: [
          "Data is kept for the time needed to handle your request and then for a maximum of three (3) years from the last contact for commercial follow-up. Contract-related data is kept in accordance with applicable legal and accounting obligations.",
        ]},
        { h: "Recipients", p: [
          "Data is intended exclusively for {COMPANY} and the technical providers strictly necessary for the site to operate (hosting, email service, booking tool), acting as processors and bound by confidentiality.",
        ]},
        { h: "Your Rights", p: [
          "Under the GDPR, you have the right of access, rectification, erasure, objection, restriction and portability of your data.",
          "You may exercise these rights at any time by writing to {EMAIL}.",
          "You also have the right to lodge a complaint with the competent supervisory authority (in France, the CNIL — www.cnil.fr).",
        ]},
        { h: "Security", p: [
          "All reasonable technical and organisational measures are implemented to protect your personal data against loss, unauthorised access, disclosure or alteration.",
        ]},
      ],
    },
  },

  cookies: {
    fr: {
      seoTitle: "Politique des cookies — O'Lime Design",
      seoDesc: "Politique des cookies d'O'Lime Design : types de cookies utilisés et gestion de votre consentement.",
      updated: "Juin 2026",
      sections: [
        { h: "Qu’est-ce qu’un cookie ?", p: [
          "Un cookie est un petit fichier déposé sur votre appareil lors de la visite d’un site. Il permet notamment d’assurer le bon fonctionnement du site et de mémoriser certaines préférences.",
        ]},
        { h: "Cookies nécessaires", p: [
          "Indispensables au fonctionnement du site (navigation, sécurité, mémorisation de vos choix de consentement). Ils ne peuvent pas être désactivés.",
        ]},
        { h: "Cookies fonctionnels", p: [
          "Ils mémorisent vos préférences, comme la langue d’affichage, afin d’améliorer votre expérience.",
        ]},
        { h: "Cookies analytiques", p: [
          "Ils permettent de mesurer l’audience du site de manière anonyme. Ils ne sont déposés qu’avec votre consentement.",
        ]},
        { h: "Cookies tiers", p: [
          "Certains services externes (ex. réseaux sociaux, outils intégrés) peuvent déposer leurs propres cookies. Ils ne sont activés qu’avec votre consentement.",
        ]},
        { h: "Gérer votre consentement", p: [
          "Vous pouvez accepter, refuser ou personnaliser les cookies non essentiels et modifier votre choix à tout moment via le lien « Gérer les cookies » présent dans le pied de page.",
        ]},
      ],
    },
    it: {
      seoTitle: "Politica dei cookie — O'Lime Design",
      seoDesc: "Politica dei cookie di O'Lime Design: tipi di cookie utilizzati e gestione del consenso.",
      updated: "Giugno 2026",
      sections: [
        { h: "Cos’è un cookie?", p: [
          "Un cookie è un piccolo file salvato sul vostro dispositivo durante la visita di un sito. Consente in particolare di garantire il corretto funzionamento del sito e di memorizzare alcune preferenze.",
        ]},
        { h: "Cookie necessari", p: [
          "Indispensabili al funzionamento del sito (navigazione, sicurezza, memorizzazione delle scelte di consenso). Non possono essere disattivati.",
        ]},
        { h: "Cookie funzionali", p: [
          "Memorizzano le vostre preferenze, come la lingua di visualizzazione, per migliorare l’esperienza.",
        ]},
        { h: "Cookie analitici", p: [
          "Consentono di misurare l’audience del sito in modo anonimo. Vengono installati solo con il vostro consenso.",
        ]},
        { h: "Cookie di terze parti", p: [
          "Alcuni servizi esterni (es. social network, strumenti integrati) possono installare i propri cookie. Vengono attivati solo con il vostro consenso.",
        ]},
        { h: "Gestire il consenso", p: [
          "Potete accettare, rifiutare o personalizzare i cookie non essenziali e modificare la vostra scelta in qualsiasi momento tramite il link « Gestisci i cookie » nel piè di pagina.",
        ]},
      ],
    },
    en: {
      seoTitle: "Cookies Policy — O'Lime Design",
      seoDesc: "O'Lime Design cookies policy: the types of cookies used and how to manage your consent.",
      updated: "June 2026",
      sections: [
        { h: "What is a cookie?", p: [
          "A cookie is a small file placed on your device when you visit a website. It notably helps the site work properly and remembers certain preferences.",
        ]},
        { h: "Necessary cookies", p: [
          "Essential for the site to function (navigation, security, remembering your consent choices). They cannot be disabled.",
        ]},
        { h: "Functional cookies", p: [
          "They remember your preferences, such as the display language, to improve your experience.",
        ]},
        { h: "Analytics cookies", p: [
          "They allow the site audience to be measured anonymously. They are only set with your consent.",
        ]},
        { h: "Third-party cookies", p: [
          "Some external services (e.g. social networks, embedded tools) may set their own cookies. They are only enabled with your consent.",
        ]},
        { h: "Managing your consent", p: [
          "You can accept, decline or customise non-essential cookies and change your choice at any time via the “Manage cookies” link in the footer.",
        ]},
      ],
    },
  },

  cgv: {
    fr: {
      seoTitle: "Conditions générales de vente — O'Lime Design",
      seoDesc: "Conditions générales de vente d'O'Lime Design : prestations, devis, tarifs, paiement et droit applicable.",
      updated: "Juin 2026",
      sections: [
        { h: "Objet", p: [
          "Les présentes conditions générales de vente régissent les prestations de décoration d’intérieur, scénographie, wedding et event design proposées par {COMPANY}.",
        ]},
        { h: "Devis et commande", p: [
          "Toute prestation fait l’objet d’un devis détaillé. La commande est ferme après acceptation écrite du devis par le client.",
        ]},
        { h: "Tarifs et paiement", p: [
          "Les tarifs sont indiqués dans le devis. Les modalités de paiement (acompte, échéances, moyens acceptés) sont précisées sur le devis. — À compléter.",
        ]},
        { h: "Délais et exécution", p: [
          "Les délais de réalisation sont communiqués à titre indicatif dans le devis et peuvent varier selon la disponibilité des prestataires et fournisseurs.",
        ]},
        { h: "Droit de rétractation", p: [
          "Conformément au Code de la consommation, le client consommateur dispose d’un délai de rétractation de quatorze (14) jours pour les contrats conclus à distance, sauf exceptions légales (notamment prestations personnalisées ou pleinement exécutées avant la fin du délai avec accord du client). — À compléter selon vos modalités.",
        ]},
        { h: "Propriété intellectuelle", p: [
          "Les créations, concepts, visuels et documents remis restent la propriété de {COMPANY} jusqu’au paiement intégral et ne peuvent être reproduits sans autorisation.",
        ]},
        { h: "Droit applicable", p: [
          "Les présentes CGV sont soumises au droit français. Tout litige relève de la compétence des juridictions françaises.",
        ]},
      ],
    },
    it: {
      seoTitle: "Condizioni generali di vendita — O'Lime Design",
      seoDesc: "Condizioni generali di vendita di O'Lime Design: servizi, preventivi, tariffe, pagamento e legge applicabile.",
      updated: "Giugno 2026",
      sections: [
        { h: "Oggetto", p: [
          "Le presenti condizioni generali di vendita disciplinano i servizi di decorazione d’interni, scenografia, wedding ed event design offerti da {COMPANY}.",
        ]},
        { h: "Preventivo e ordine", p: [
          "Ogni servizio è oggetto di un preventivo dettagliato. L’ordine è definitivo previa accettazione scritta del preventivo da parte del cliente.",
        ]},
        { h: "Tariffe e pagamento", p: [
          "Le tariffe sono indicate nel preventivo. Le modalità di pagamento (acconto, scadenze, mezzi accettati) sono precisate nel preventivo. — Da completare.",
        ]},
        { h: "Tempistiche ed esecuzione", p: [
          "I tempi di realizzazione sono indicati a titolo orientativo nel preventivo e possono variare in base alla disponibilità di fornitori e collaboratori.",
        ]},
        { h: "Diritto di recesso", p: [
          "Secondo la normativa applicabile, il cliente consumatore dispone di un termine di recesso di quattordici (14) giorni per i contratti conclusi a distanza, salvo le eccezioni di legge (in particolare servizi personalizzati o interamente eseguiti prima della scadenza con accordo del cliente). — Da completare secondo le vostre modalità.",
        ]},
        { h: "Proprietà intellettuale", p: [
          "Le creazioni, i concept, i visual e i documenti forniti restano proprietà di {COMPANY} fino al saldo integrale e non possono essere riprodotti senza autorizzazione.",
        ]},
        { h: "Legge applicabile", p: [
          "Le presenti condizioni sono soggette al diritto francese. Ogni controversia è di competenza dei tribunali francesi.",
        ]},
      ],
    },
    en: {
      seoTitle: "Terms & Conditions — O'Lime Design",
      seoDesc: "O'Lime Design terms & conditions: services, quotes, pricing, payment and applicable law.",
      updated: "June 2026",
      sections: [
        { h: "Purpose", p: [
          "These terms and conditions govern the interior decoration, scenography, wedding and event design services offered by {COMPANY}.",
        ]},
        { h: "Quote and Order", p: [
          "Each service is subject to a detailed quote. The order is firm once the client accepts the quote in writing.",
        ]},
        { h: "Pricing and Payment", p: [
          "Prices are stated in the quote. Payment terms (deposit, instalments, accepted methods) are set out in the quote. — To be completed.",
        ]},
        { h: "Timeframes and Delivery", p: [
          "Delivery times are provided for guidance in the quote and may vary depending on the availability of providers and suppliers.",
        ]},
        { h: "Right of Withdrawal", p: [
          "Under applicable consumer law, a consumer client has a fourteen (14) day withdrawal period for contracts concluded at a distance, subject to legal exceptions (in particular bespoke services or services fully performed before the end of the period with the client’s agreement). — To be completed according to your terms.",
        ]},
        { h: "Intellectual Property", p: [
          "Creations, concepts, visuals and documents provided remain the property of {COMPANY} until full payment and may not be reproduced without authorisation.",
        ]},
        { h: "Applicable Law", p: [
          "These terms are governed by French law. Any dispute falls under the jurisdiction of the French courts.",
        ]},
      ],
    },
  },
};

const interpolate = (str, cfg) =>
  str
    .replace(/{COMPANY}/g, cfg.company || "O'Lime Design")
    .replace(/{FOUNDER}/g, cfg.founder || "Marie Mazzone")
    .replace(/{SIRET}/g, cfg.siret || "—")
    .replace(/{APE}/g, cfg.ape || "7410Z")
    .replace(/{VAT}/g, cfg.vat || "—")
    .replace(/{EMAIL}/g, cfg.email || "—")
    .replace(/{PHONE}/g, cfg.phone || "—")
    .replace(/{CITY}/g, cfg.city || "Rueil-Malmaison, France")
    .replace(/{HOST}/g, cfg.host_name || "OVHcloud")
    .replace(/{HOST_ADDR}/g, cfg.host_address || "2 rue Kellermann, 59100 Roubaix, France");

export const getLegalDoc = (type, lang, cfg = {}) => {
  const t = DOCS[type] ? type : "mentions";
  const l = DOCS[t][lang] ? lang : "fr";
  const doc = DOCS[t][l];
  return {
    ...doc,
    sections: doc.sections.map((s) => ({
      h: s.h,
      p: s.p.map((par) => interpolate(par, cfg)),
    })),
  };
};
